import React, { createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useServiceAccess from "../hooks/useServiceAccess";
import MembershipRedirectOverlay from "./MembershipRedirectOverlay";
import { useAuth } from "../Context/AuthContext";
import { SERVICE_ENTITY_REGISTRY, isGenuineEntityField } from "../config/serviceEntityRegistry";

// Context so children can read their access level without prop-drilling
const ServiceAccessContext = createContext({ level: "FULL", limit: null });
export const useServiceAccessContext = () => useContext(ServiceAccessContext);

const BANNER_CONFIG = {
  READ_ONLY: {
    bg: "#e8f0fe", border: "#4285f4", icon: "👁",
    text: "You have read-only access to this service. Edit and create actions are disabled.",
  },
};

function AccessBanner({ level }) {
  const cfg = BANNER_CONFIG[level];
  if (!cfg) return null;
  const text = cfg.text;
  return (
    <div style={{
      background: cfg.bg, borderLeft: `4px solid ${cfg.border}`,
      padding: "10px 16px", borderRadius: 4, marginBottom: 16,
      display: "flex", alignItems: "center", gap: 10, fontSize: 13,
    }}>
      <span>{cfg.icon}</span>
      <span>{text}</span>
    </div>
  );
}

/**
 * ServiceGuard — wraps a service page and enforces the accessibility level.
 *
 * Props:
 *   accessKey   {string}   — accessibility rule key, e.g. "MY_ORG"
 *   serviceKey  {string}   — route key, e.g. "myFpo"
 *   serviceName {string}   — display name shown in overlays
 *   children    {ReactNode}
 *
 * Behaviour by level:
 *   HIDDEN             → hard block, renders MembershipRedirectOverlay (no children). Home-grid
 *                        visibility is filtered separately in useVisibleServices.js; this is the
 *                        direct-URL-entry backstop.
 *   SUBSCRIBE_REQUIRED → redirects to this service's subscribe flow. Two destinations: if
 *                        `accessKey` is a registered entity-service (SERVICE_ENTITY_REGISTRY)
 *                        whose home usertype differs from the account's own, this is a Case-2
 *                        cross-service subscribe (see the cross-service access plan) - a
 *                        separate, independent registration+subscription, not an upgrade of the
 *                        account's existing plan - so it goes straight to that service's
 *                        registration form (which bundles its own plan choice) instead of the
 *                        generic plan carousel. Every other service keeps going to the carousel.
 *                        Once that registration has been submitted (fullUserData.
 *                        pendingSubscriptionPlanId is set - see CompanyServiceImpl.
 *                        addCompanyToUser/FPOServiceImpl.addFPOToUser), it goes to the carousel
 *                        too instead of back to the registration form - the carousel's own
 *                        pending-request detection (SubscriptionPage.jsx) shows "Request Pending
 *                        Approval" there, the same screen a same-usertype pending subscriber
 *                        already sees, rather than re-showing an already-submitted form. Likewise
 *                        skipped when the entity already exists (registryEntry.entityFields.some on
 *                        fullUserData - registration completed at some point in the past, whether or
 *                        not a request is pending right now) - an admin flipping this usertype's
 *                        config to SUBSCRIBE_REQUIRED after the fact must not re-send an already-set-up
 *                        account back through entity creation.
 *                        Skipped entirely when already sitting on that exact registration route -
 *                        this guard re-evaluates on every /mybusiness/* sub-path (ServicesLayout
 *                        wraps its whole <Outlet/> in one ServiceGuard per accessKey), so without
 *                        this check it would keep replacing /mybusiness/setup with a Navigate
 *                        back to /mybusiness/setup - a self-redirect that renders blank instead of
 *                        ever letting the setup form mount (mirrors ProtectedRoutes.jsx's
 *                        isEntitySetupPath exception, which guards the same scenario one layer up).
 *   READ_ONLY          → renders children + banner; context level = READ_ONLY
 *   FULL               → renders children, no banner
 */
export default function ServiceGuard({ accessKey, serviceKey, serviceName, children }) {
  const { level, limit } = useServiceAccess(accessKey);
  const { userTypeInfo, fullUserData } = useAuth();
  const location = useLocation();

  if (level === "HIDDEN") {
    return <MembershipRedirectOverlay serviceName={serviceName} />;
  }

  const registryEntry = SERVICE_ENTITY_REGISTRY[accessKey];
  const isOnOwnSetupRoute = registryEntry?.setupRoute === location.pathname;
  const hasPendingRequest = !!fullUserData?.pendingSubscriptionPlanId;
  // Registration for this service was already completed in the past (company/FPO entity exists),
  // as opposed to merely having a request in flight right now - same entityFields.some() check
  // AuthContext.pendingEntitySetups/userTypeContexts use to detect "this usertype is already held".
  // Without this, an account whose registration finished long ago (pendingSubscriptionPlanId is
  // correctly null - nothing pending) got misread as "never registered" the moment an admin flipped
  // this usertype's config to SUBSCRIBE_REQUIRED, and was bounced back into the entity setup form to
  // re-enter company details that already exist. Confirmed live.
  const hasExistingEntity = !!registryEntry?.entityFields?.some(
    (field) => !!fullUserData?.[field] && isGenuineEntityField(registryEntry.serviceKey, field, userTypeInfo?.code)
  );

  if (level === "SUBSCRIBE_REQUIRED" && !isOnOwnSetupRoute) {
    const isCrossUsertypeSubscribe =
      registryEntry?.homeUserTypeCode && registryEntry.homeUserTypeCode !== userTypeInfo?.code;
    if (isCrossUsertypeSubscribe && !hasPendingRequest && !hasExistingEntity) {
      return <Navigate to={registryEntry.setupRoute} replace />;
    }
    return <Navigate to={`/home/subscription?service=${serviceKey}`} replace />;
  }

  return (
    <ServiceAccessContext.Provider value={{ level, limit }}>
      {level !== "FULL" && <AccessBanner level={level} />}
      {children}
    </ServiceAccessContext.Provider>
  );
}
